import { omit } from 'lodash'
import baseApi from '@core/api'
import type {
    AddressClosestData,
    AddressClosestResponse,
    AddressCreateData,
    AddressCreateResponse,
    AddressDeleteData,
    AddressDeleteResponse,
    AddressDetailsData,
    AddressDetailsResponse,
    AddressListResponse,
    AddressUpdateData,
    AddressUpdateResponse,
    InvoiceInfoCreateData,
    InvoiceInfoCreateResponse,
    InvoiceInfoDeleteData,
    InvoiceInfoDeleteResponse,
    InvoiceInfoDetailsData,
    InvoiceInfoDetailsResponse,
    InvoiceInfoListResponse,
    InvoiceInfoUpdateData,
    InvoiceInfoUpdateResponse,
    InvoicePDFData,
    InvoicePDFResponse,
    InvoicesData,
    InvoicesResponse,
} from './UserApi.types'

const userApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        addressClosest: builder.query<
            AddressClosestResponse,
            AddressClosestData
        >({
            query: data => ({
                url: '/user/address/closest',
                method: 'GET',
                params: data,
            }),
        }),
        addressList: builder.query<AddressListResponse, void>({
            query: () => ({
                url: '/user/address/list',
                method: 'GET',
            }),
            providesTags: ['AddressList'],
        }),
        addressCreate: builder.mutation<
            AddressCreateResponse,
            AddressCreateData
        >({
            query: data => ({
                url: '/user/address/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AddressList']),
        }),
        addressDetails: builder.query<
            AddressDetailsResponse,
            AddressDetailsData
        >({
            query: data => ({
                url: `/user/address/details/${data.id}`,
                method: 'GET',
            }),
            providesTags: ['AddressDetail'],
        }),
        addressUpdate: builder.mutation<
            AddressUpdateResponse,
            AddressUpdateData
        >({
            query: data => ({
                url: `/user/address/update/${data.id}`,
                method: 'PUT',
                body: omit(data, 'id'),
            }),
            invalidatesTags: (_, error) =>
                error ? [] : ['AddressList', 'AddressDetail'],
        }),
        addressDelete: builder.mutation<
            AddressDeleteResponse,
            AddressDeleteData
        >({
            query: data => ({
                url: `/user/address/delete/${data.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) => (error ? [] : ['AddressList']),
        }),
        invoiceInfoList: builder.query<InvoiceInfoListResponse, void>({
            query: () => ({
                url: '/user/invoice-info/list',
                method: 'GET',
            }),
            providesTags: ['InvoiceInfoList'],
        }),
        invoiceInfoDetails: builder.query<
            InvoiceInfoDetailsResponse,
            InvoiceInfoDetailsData
        >({
            query: data => ({
                url: `/user/invoice-info/details/${data.id}`,
                method: 'GET',
            }),
            providesTags: ['InvoiceInfoDetail'],
        }),
        invoiceInfoCreate: builder.mutation<
            InvoiceInfoCreateResponse,
            InvoiceInfoCreateData
        >({
            query: data => ({
                url: '/user/invoice-info/create',
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_, error) => (error ? [] : ['InvoiceInfoList']),
        }),
        invoiceInfoUpdate: builder.mutation<
            InvoiceInfoUpdateResponse,
            InvoiceInfoUpdateData
        >({
            query: data => ({
                url: `/user/invoice-info/update/${data.id}`,
                method: 'PUT',
                body: omit(data, 'id'),
            }),
            invalidatesTags: (_, error) =>
                error ? [] : ['InvoiceInfoList', 'InvoiceInfoDetail'],
        }),
        invoiceInfoDelete: builder.mutation<
            InvoiceInfoDeleteResponse,
            InvoiceInfoDeleteData
        >({
            query: data => ({
                url: `/user/invoice-info/delete/${data.id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, error) => (error ? [] : ['InvoiceInfoList']),
        }),
        invoicesList: builder.query<InvoicesResponse, InvoicesData>({
            query: data => ({
                url: '/user/invoice/list',
                method: 'GET',
                params: data,
            }),
            serializeQueryArgs: ({
                endpointName,
                queryArgs: { ...queryArgs },
            }) => {
                return { endpointName, ...queryArgs }
            },
            merge: (currentData, newData, { arg }) => {
                if (!arg.page || arg.page === 1) return newData

                currentData.data.push(...newData.data)
                currentData.meta.paginate = newData.meta.paginate
            },
            keepUnusedDataFor: 0,
        }),
        invoicePDF: builder.query<InvoicePDFResponse, InvoicePDFData>({
            query: data => ({
                url: `/user/invoice/pdf/${data.id}`,
                method: 'GET',
                responseHandler: response => {
                    if (response.ok) return response.arrayBuffer()
                    return response.json()
                },
            }),
        }),
    }),
    overrideExisting: true,
})

export default userApi
